// Single source of truth for reading a dish's nutrition.
//
// Two formats reach this app and they disagree about what the numbers mean:
//
//   * Hand-entered products (entrées, desserts, drinks, and the bundled
//     offline data) come off a pack label and are PER 100 G, keyed
//     energy/fat/sat/carbs/sugar/fiber/protein/salt.
//   * Weekly-food.ch meals are scraped from the supplier's own table, which
//     is published PER PORTION ("Per serving of 450 grams"), keyed
//     kcal/protein/carbs/fat/saturated/fibre/salt.
//
// The product page used to multiply whatever it found by weight/100, so
// supplier figures got scaled a second time — 71.7 g of protein rendered as
// 322.7 g — while `saturated` and `fibre` missed the per-100 g key names and
// rendered as dashes.
//
// The API now returns `nutritionPerMeal` already computed, so normally there
// is no arithmetic to do here at all. Everything below is the fallback for
// the bundled offline data and for any response cached before the fix.
// Nothing else in the app should scale nutrition by weight.

export const NUTRITION_KEYS = ['energy', 'fat', 'sat', 'carbs', 'sugar', 'fiber', 'protein', 'salt'];

const ALIASES = {
  energy: 'energy', kcal: 'energy', calories: 'energy', energie: 'energy',
  energy_calories: 'energy', energy_kcal: 'energy',
  fat: 'fat', fats: 'fat', lipides: 'fat', total_fat: 'fat', graisses: 'fat',
  matieres_grasses: 'fat',
  sat: 'sat', saturated: 'sat', saturates: 'sat', satures: 'sat',
  saturated_fat: 'sat', acides_gras_satures: 'sat', fatty_acids_saturated: 'sat',
  carbs: 'carbs', carbohydrates: 'carbs', carbohydrate: 'carbs', glucides: 'carbs',
  carbohydrates_available: 'carbs',
  sugar: 'sugar', sugars: 'sugar', sucres: 'sugar', dont_sucres: 'sugar',
  fiber: 'fiber', fibre: 'fiber', fibres: 'fiber',
  dietary_fibres: 'fiber', dietary_fibre: 'fiber', fibres_alimentaires: 'fiber',
  protein: 'protein', proteins: 'protein', proteines: 'protein',
  salt: 'salt', sel: 'salt',
};

const canonKey = (k) =>
  ALIASES[String(k).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')];

const num = (v) => {
  if (v === null || v === undefined || typeof v === 'boolean') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const m = String(v).match(/-?\d+(?:[.,]\d+)?/);
  if (!m) return null;
  const n = parseFloat(m[0].replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

// Canonical keys, numbers only. A value the supplier never published is left
// out rather than set to 0 — "not stated" and "contains none" differ.
export function canonicalValues(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.keys(raw).forEach((k) => {
    const key = canonKey(k);
    if (!key || out[key] !== undefined) return;
    const n = num(raw[k]);
    if (n !== null) out[key] = n;
  });
  return out;
}

// Stored basis wins; only never-stamped data falls back to its origin.
export function resolveBasis(dish) {
  const stored = String(dish?.nutritionBasis || '').trim().toLowerCase();
  if (stored === 'portion' || stored === '100g') return stored;
  return dish?.source === 'sync' ? 'portion' : '100g';
}

/**
 * What to print for one dish.
 *
 * scope 'meal'  — values describe one whole item (use the portion weight in the caption)
 * scope '100g'  — per-100 g values only, because no net weight is recorded
 * scope null    — nothing to show
 *
 * `missing` lists canonical keys with no value, so the page can say so
 * instead of printing a dash that looks like a measurement.
 */
export function mealNutrition(dish) {
  const empty = { values: {}, scope: null, weight: null, missing: NUTRITION_KEYS };
  if (!dish) return empty;

  const served = canonicalValues(dish.nutritionPerMeal);
  const weight = num(dish.weight);
  if (Object.keys(served).length) {
    return { values: served, scope: 'meal', weight, missing: NUTRITION_KEYS.filter((k) => served[k] === undefined) };
  }

  const stored = canonicalValues(dish.nutrition);
  if (!Object.keys(stored).length) return empty;

  const basis = resolveBasis(dish);
  if (basis === 'portion') {
    return { values: stored, scope: 'meal', weight, missing: NUTRITION_KEYS.filter((k) => stored[k] === undefined) };
  }
  if (weight && weight > 0) {
    const factor = weight / 100;
    const scaled = {};
    Object.keys(stored).forEach((k) => { scaled[k] = Math.round(stored[k] * factor * 10) / 10; });
    return { values: scaled, scope: 'meal', weight, missing: NUTRITION_KEYS.filter((k) => scaled[k] === undefined) };
  }
  // Per-100 g values and no weight: show them as per 100 g rather than guess.
  return { values: stored, scope: '100g', weight: null, missing: NUTRITION_KEYS.filter((k) => stored[k] === undefined) };
}

// Energy for one whole item — what the cards print. Falls back to the stored
// `kcal` field only when there is no nutrition table to derive it from.
export function mealKcal(dish) {
  const { values, scope } = mealNutrition(dish);
  if (scope === 'meal' && values.energy !== undefined) return Math.round(values.energy);
  if (scope === null) {
    const k = num(dish?.kcal);
    return k === null ? null : Math.round(k);
  }
  return null;
}

export const formatNutrient = (key, value) =>
  key === 'energy' ? `${Math.round(value)} kcal` : `${Math.round(value * 10) / 10} g`;
